import type { Id } from "@repo/convex/dataModel";
import { ArrowLeft, CircleCheck, RotateCcw, UserRoundCheck } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { ClaudiaIcon } from "@/components/claudia-icon";
import { Button } from "@/components/ui/button";
import { MessageMarkdown } from "@/components/ui/message-markdown";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import {
  clarificationInputDefaults,
  MessageRichTextInput,
  type MessageRichTextSubmitPayload,
} from "@/features/rich-text";
import { trackClarificationEvent, trackVoiceEvent } from "@/lib/analytics/domain";
import { formatSmartDate } from "@/lib/formatters";
import { AssignmentBar } from "./assignment-bar";
import {
  ConsultationClosedEntry,
  ConsultationStartedEntry,
  ConsultationStatusEntry,
  ConsultationSummaryEntry,
} from "./consultation-entries";
import type { ConsultationView } from "./consultation-types";
import { TeamsIcon } from "./teams-icon";
import { TriageStatusBadge } from "./triage-status-badge";

/** Live (non-terminal) display statuses get a status entry pinned to the bottom. */
const LIVE_STATUS_SET = new Set<ConsultationView["displayStatus"]>([
  "setting_up",
  "waiting",
  "in_progress",
  "needs_attention",
]);

type MessageSource = "app" | "teams" | "claudia";
type ClarificationStatus = "open" | "closed";

type LocalMessage = {
  id: string;
  content: string;
  author: string;
  authorInitials: string;
  timestamp: Date;
  source: MessageSource;
};

export type AssignmentEvent = {
  id: string;
  timestamp: Date;
  assignedByName: string;
  assignedToName: string;
};

type TimelineItem =
  | { kind: "message"; sortKey: number; data: LocalMessage }
  | { kind: "assignment"; sortKey: number; data: AssignmentEvent }
  | { kind: "consultation_started"; sortKey: number; data: ConsultationView }
  | { kind: "consultation_closed"; sortKey: number; data: ConsultationView };

const TRIAGE_WORKING_MESSAGE = "Claudia is checking the project for an answer.";

export type ClarificationChatHeaderRenderContext = {
  isResolved: boolean;
  onBack: () => void;
  onToggleStatus: () => void;
  isUpdatingStatus: boolean;
};

interface ClarificationChatViewProps {
  mentionProjectId: Id<"projects">;
  selectedClarification: {
    id: string;
    title: string;
    status: ClarificationStatus;
    author: string;
    authorInitials: string;
    createdAt: Date;
    highlightedText?: string;
  } | null;
  messages: LocalMessage[];
  /** Append-only assignment history, oldest first. Interleaved with messages. */
  assignmentEvents?: AssignmentEvent[];
  /** Current owner of the clarification request; controls the AssignmentBar variant. */
  ownerId?: Id<"users"> | null;
  /** Called when the user picks a new owner from the AssignmentBar's dropdown. */
  onAssignOwner?: (userId: Id<"users">) => void | Promise<void>;
  isAssigningOwner?: boolean;
  onSendMessage: (
    payload: MessageRichTextSubmitPayload
  ) => boolean | Promise<boolean | undefined> | undefined;
  isSendingMessage?: boolean;
  composerAuthor: string;
  composerInitials?: string;
  onStatusChange?: (status: ClarificationStatus) => void | Promise<void>;
  isUpdatingStatus?: boolean;
  onBack: () => void;
  surface: "full_screen" | "right_panel";
  /**
   * Optional custom header rendered above the navigation row. When provided,
   * callers can swap the default "Back · Resolve" affordances for a richer
   * context block (e.g. project-wide page showing entity icon + field name).
   * If omitted, the default Back + Resolve toolbar is rendered.
   */
  renderHeader?: (ctx: ClarificationChatHeaderRenderContext) => React.ReactNode;
  /** Teams consultations projected for the timeline. */
  consultations?: ConsultationView[];
  /** Whether Claudia triage is currently queued/running for this request. */
  isTriageActive?: boolean;
  /** Rendered in the default header's action row (e.g. Invite via Teams). */
  headerInviteSlot?: React.ReactNode;
}

export function ClarificationChatView({
  mentionProjectId,
  selectedClarification,
  messages,
  assignmentEvents = [],
  ownerId,
  onAssignOwner,
  isAssigningOwner = false,
  onSendMessage,
  isSendingMessage = false,
  composerAuthor,
  onStatusChange,
  isUpdatingStatus = false,
  onBack,
  surface,
  renderHeader,
  consultations = [],
  isTriageActive = false,
  headerInviteSlot,
}: ClarificationChatViewProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pendingVoiceTranscriptionCountRef = useRef(0);

  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [
      ...messages.map(
        (m): TimelineItem => ({
          kind: "message",
          sortKey: m.timestamp.getTime(),
          data: m,
        })
      ),
      ...assignmentEvents.map(
        (e): TimelineItem => ({
          kind: "assignment",
          sortKey: e.timestamp.getTime(),
          data: e,
        })
      ),
    ];
    // Consultation events interleave chronologically: an anchored
    // "invited X" marker for every consultation, plus a muted record line when
    // one is cancelled/closed. The live status entry is NOT here — it floats at
    // the bottom (see liveStatusConsultations) until the consultation concludes.
    for (const consultation of consultations) {
      items.push({
        kind: "consultation_started",
        sortKey: consultation.startedAt.getTime(),
        data: consultation,
      });
      if (consultation.displayStatus === "cancelled" || consultation.displayStatus === "closed") {
        items.push({
          kind: "consultation_closed",
          sortKey: (consultation.endedAt ?? consultation.startedAt).getTime(),
          data: consultation,
        });
      }
    }
    items.sort((a, b) => a.sortKey - b.sortKey);
    return items;
  }, [messages, assignmentEvents, consultations]);

  // A concluded consultation's summary is a real claudia message already in the
  // timeline; map it by id so it renders as the rich "Answered" entry in place.
  const summaryByMessageId = useMemo(() => {
    const map = new Map<string, ConsultationView>();
    for (const consultation of consultations) {
      if (consultation.summaryMessageId) {
        map.set(consultation.summaryMessageId, consultation);
      }
    }
    return map;
  }, [consultations]);

  const liveStatusConsultations = useMemo(
    () => consultations.filter((consultation) => LIVE_STATUS_SET.has(consultation.displayStatus)),
    [consultations]
  );

  // Auto-scroll when the thread grows (messages, events, or live status entries).
  const timelineCount = timeline.length + liveStatusConsultations.length + (isTriageActive ? 1 : 0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally only scroll on count change, not content changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [timelineCount]);

  const formatMessageTime = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatAssignmentTime = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const authorTime = selectedClarification
    ? formatSmartDate(selectedClarification.createdAt.getTime())
    : "";
  const isResolved = selectedClarification?.status === "closed";

  const handleToggleStatus = () => {
    if (!selectedClarification) return;
    const nextStatus = selectedClarification.status === "open" ? "closed" : "open";
    void onStatusChange?.(nextStatus);
  };

  const handleSendMessage = async (payload: MessageRichTextSubmitPayload) => {
    const usedVoiceInput = pendingVoiceTranscriptionCountRef.current > 0;
    const didSend = await onSendMessage(payload);
    if (!didSend || !selectedClarification) return didSend;

    trackClarificationEvent("clarification_message_sent", {
      surface,
      project_id: mentionProjectId,
      clarification_id: selectedClarification.id,
      has_voice_input: usedVoiceInput,
    });
    if (usedVoiceInput) {
      trackVoiceEvent("voice_transcript_used", {
        source: "clarification",
        surface,
        project_id: mentionProjectId,
        target_action: "clarification_message_sent",
      });
      pendingVoiceTranscriptionCountRef.current = 0;
    }

    return didSend;
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <ScrollShadow
        className="min-h-0 flex-1"
        colorScheme="raised"
        contentClassName="min-h-full flex flex-col"
      >
        {/* Header — caller-supplied or default Back/Resolve row */}
        {renderHeader ? (
          renderHeader({
            isResolved,
            onBack,
            onToggleStatus: handleToggleStatus,
            isUpdatingStatus,
          })
        ) : (
          <div className="flex items-center gap-3 px-4 py-2 shrink-0">
            <button
              type="button"
              onClick={onBack}
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
              aria-label="Back to list"
            >
              <ArrowLeft className="size-3.5" />
              <span className="text-xs font-medium">Back</span>
            </button>

            <div className="flex-1 min-w-0" />

            {headerInviteSlot}

            {/* Resolve / Reopen button */}
            {selectedClarification && (
              <Button
                type="button"
                size="xs"
                variant="outline"
                className="gap-1"
                aria-label={`${isResolved ? "Reopen" : "Resolve"} this clarification request`}
                disabled={isUpdatingStatus}
                onClick={handleToggleStatus}
              >
                {isResolved ? <RotateCcw className="size-3" /> : <CircleCheck className="size-3" />}
                <span className="text-[11px] font-medium">{isResolved ? "Reopen" : "Resolve"}</span>
              </Button>
            )}
          </div>
        )}

        <div
          role="log"
          aria-live="polite"
          aria-label="Chat messages"
          className="ph-no-capture flex-1 py-2"
        >
          {/* Original clarification */}
          {selectedClarification && (
            <div className="py-2.5 px-4">
              <div className="flex items-center gap-2.5 mb-1.5">
                <span className="text-foreground text-xs font-semibold">
                  {selectedClarification.author}
                </span>
                <span className="flex-1" />
                <span className="text-muted-foreground text-xs">{authorTime}</span>
              </div>
              {selectedClarification.highlightedText && (
                <p className="text-muted-foreground mb-1.5 line-clamp-4 border-l-2 border-border pl-2 text-xs italic">
                  {selectedClarification.highlightedText}
                </p>
              )}
              <MessageMarkdown
                content={selectedClarification.title}
                className="text-foreground/80 text-[13px] leading-[1.4] [&_p]:mb-0"
              />
            </div>
          )}

          {/* Timeline — messages, assignment events, and consultation activity
              interleaved chronologically */}
          {timeline.map((item) => {
            if (item.kind === "message") {
              const summary = summaryByMessageId.get(item.data.id);
              if (summary) {
                return (
                  <ConsultationSummaryEntry
                    key={`summary:${item.data.id}`}
                    consultation={summary}
                    content={item.data.content}
                    timestamp={item.data.timestamp}
                  />
                );
              }
              return (
                <article
                  key={`m:${item.data.id}`}
                  aria-label={`Message from ${item.data.author}`}
                  className="border-border border-t px-4 py-2.5"
                >
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <span className="text-foreground text-xs font-semibold">
                      {item.data.author}
                    </span>
                    {item.data.source === "teams" && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5">
                        <TeamsIcon size={12} />
                        Teams
                      </span>
                    )}
                    {item.data.source === "claudia" && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                        <ClaudiaIcon className="size-3" aria-hidden />
                        <span>Claudia</span>
                      </span>
                    )}
                    <span className="flex-1" />
                    <time
                      dateTime={item.data.timestamp.toISOString()}
                      className="text-muted-foreground text-xs"
                    >
                      {formatMessageTime(item.data.timestamp)}
                    </time>
                  </div>
                  <MessageMarkdown
                    content={item.data.content}
                    className="text-foreground/80 text-xs"
                  />
                </article>
              );
            }
            if (item.kind === "consultation_started") {
              return (
                <ConsultationStartedEntry key={`cstart:${item.data.id}`} consultation={item.data} />
              );
            }
            if (item.kind === "consultation_closed") {
              return (
                <ConsultationClosedEntry key={`cclose:${item.data.id}`} consultation={item.data} />
              );
            }
            return (
              <div
                key={`a:${item.data.id}`}
                role="note"
                aria-label="Assignment change"
                className="border-border border-t flex items-center gap-1.5 px-4 py-2"
              >
                <UserRoundCheck className="size-3 text-neutral-dim" />
                <span className="text-[10px] text-neutral-dim">
                  {item.data.assignedByName} assigned {item.data.assignedToName} ·{" "}
                  {formatAssignmentTime(item.data.timestamp)}
                </span>
              </div>
            );
          })}

          {/* Live consultation status — pinned to the bottom until concluded */}
          {liveStatusConsultations.map((consultation) => (
            <ConsultationStatusEntry key={`clive:${consultation.id}`} consultation={consultation} />
          ))}

          {isTriageActive ? <ClarificationTriageStatusEntry /> : null}

          {/* Owner assignment bar — appears between thread and composer */}
          {onAssignOwner && !isResolved ? (
            <div className="border-border border-t">
              <AssignmentBar
                ownerId={ownerId ?? null}
                onAssign={onAssignOwner}
                disabled={isAssigningOwner}
              />
            </div>
          ) : null}

          {/* Resolved banner OR reply composer */}
          {isResolved ? (
            <div className="bg-surface-green flex items-center justify-center gap-1.5 px-4 py-3">
              <CircleCheck className="text-green-content size-3.5" />
              <span className="text-green-content text-xs font-medium">
                This thread was resolved
              </span>
            </div>
          ) : (
            <div className="border-border border-t px-4 py-2.5">
              <div className="flex items-center gap-2.5 mb-1.5">
                <span className="text-foreground text-xs font-semibold">{composerAuthor}</span>
                <span className="flex-1" />
                <span className="text-muted-foreground text-xs">just now</span>
              </div>
              <MessageRichTextInput
                {...clarificationInputDefaults}
                className="!shadow-none"
                placeholder="Leave a reply..."
                disabled={isSendingMessage}
                isSubmitting={isSendingMessage}
                onSubmit={handleSendMessage}
                submitAriaLabel="Send message"
                mentionProjectId={mentionProjectId}
                voiceInput={{
                  projectId: mentionProjectId,
                  source: "clarification",
                  surface,
                  onTranscriptionCreated: () => {
                    pendingVoiceTranscriptionCountRef.current += 1;
                  },
                }}
              />
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollShadow>
    </div>
  );
}

function ClarificationTriageStatusEntry() {
  return (
    <article aria-label="Claudia is working" className="border-border border-t px-4 py-2.5">
      <div className="flex items-center gap-2.5 mb-1.5">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground">
          <ClaudiaIcon className="size-3.5" aria-hidden />
          Claudia
        </span>
        <span className="flex-1" />
        <TriageStatusBadge />
      </div>
      <p className="text-foreground/80 text-xs">{TRIAGE_WORKING_MESSAGE}</p>
    </article>
  );
}
